import { type Branding, DEFAULT_VALIDITY_DAYS, defaultQuoteTerms, type QuoteTemplate } from '@better-lights/core';
import { ImagePlus, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiMessage } from '../api/errors.ts';
import { pickFile } from '../app/projectActions.ts';
import { useBlobUrl } from '../app/useBlobUrl.ts';
import { sniffImageMime } from '../lib/mime.ts';
import { handleFeatureError } from '../plans/gate.ts';
import { services } from '../services/index.ts';
import { toast } from '../state/feedback.ts';
import { blobUri } from '../storage/blobStore.ts';
import { storage } from '../storage/index.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { LockBadge } from '../ui/LockBadge.tsx';
import { Section } from '../ui/Section.tsx';
import { Segmented } from '../ui/Segmented.tsx';
import { brandingOf, loadCatalog, saveCatalog } from './catalog.ts';

const MAX_LOGO_BYTES = 1024 * 1024;

/**
 * Quote branding (docs/spec/08 §3 "Branding data"), stored in the catalog document of `scope`
 * (personal: Starter+; company: Team owners/admins). `templates` unlocks deposit + default layout.
 */
export function BrandingEditor({ scope, canEdit, templates }: { scope: string; canEdit: boolean; templates: boolean }) {
  const [b, setB] = useState<Branding>({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<null | 'logo' | 'save'>(null);
  const logoUrl = useBlobUrl(b.logo);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    void loadCatalog(scope).then((c) => {
      if (!alive) return;
      setB(brandingOf(c) ?? {});
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [scope]);

  const set = (patch: Partial<Branding>) => setB((cur) => ({ ...cur, ...patch }));
  const field = (k: keyof Branding, label: string, extra: Record<string, unknown> = {}) => (
    <Field
      label={label}
      disabled={!canEdit}
      value={(b[k] as string | undefined) ?? ''}
      onChange={(e) => set({ [k]: e.target.value })}
      {...extra}
    />
  );

  const uploadLogo = async () => {
    const file = await pickFile('image/png,image/jpeg');
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) return toast('Logos must be 1 MB or smaller.', 'error');
    const mime = await sniffImageMime(file);
    if (mime !== 'image/png' && mime !== 'image/jpeg') return toast('Use a PNG or JPEG logo.', 'error');
    setBusy('logo');
    try {
      const sha = await storage().blobs.put(new Blob([file], { type: mime }));
      await services.sync.engine.uploadNow(scope, [sha]);
      set({ logo: blobUri(sha) });
    } catch (e) {
      if (!handleFeatureError(e, scope)) toast(apiMessage(e, 'Could not upload the logo.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!loaded) return <p className="t-body text-dim">Loading branding…</p>;
  return (
    <div className="flex flex-col gap-6">
      <Section title="Logo">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-40 items-center justify-center overflow-hidden rounded-md border border-border bg-white">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="t-caption text-muted">No logo</span>
            )}
          </div>
          <Button
            size="sm"
            icon={ImagePlus}
            disabled={!canEdit}
            loading={busy === 'logo'}
            onClick={() => void uploadLogo()}
          >
            {b.logo ? 'Replace' : 'Upload'}
          </Button>
          {b.logo && canEdit ? (
            <Button size="sm" variant="ghost" onClick={() => set({ logo: undefined })}>
              Remove
            </Button>
          ) : null}
        </div>
        <p className="t-caption text-muted">PNG or JPEG, up to 1 MB. Shown at the top of every quote.</p>
      </Section>
      <Section title="Business details">
        {field('companyName', 'Company name')}
        <div className="grid grid-cols-1 gap-3 min-[600px]:grid-cols-2">
          {field('phone', 'Phone', { type: 'tel' })}
          {field('email', 'Email', { type: 'email' })}
          {field('website', 'Website')}
          {field('licenseNumber', 'License number')}
        </div>
        {field('address', 'Address')}
      </Section>
      <Section title="Look">
        <label className="flex items-center gap-3">
          <span className="t-label">Accent color</span>
          <input
            type="color"
            aria-label="Accent color"
            disabled={!canEdit}
            value={b.accentColor ?? '#FFB547'}
            onChange={(e) => set({ accentColor: e.target.value.toUpperCase() })}
            className="h-9 w-14 cursor-pointer rounded-sm border border-border bg-transparent"
          />
          <span className="t-caption tabular text-dim">{b.accentColor ?? '#FFB547'}</span>
        </label>
        <div className="flex items-center justify-between gap-3">
          <span className="t-label">Default layout</span>
          {templates ? null : <LockBadge flag="quote_templates" />}
        </div>
        <Segmented<QuoteTemplate>
          label="Default layout"
          value={b.template ?? 'classic'}
          onChange={(t) => canEdit && templates && set({ template: t })}
          options={[
            { value: 'classic', label: 'Classic' },
            { value: 'modern', label: 'Modern' },
            { value: 'minimal', label: 'Minimal' },
          ]}
        />
      </Section>
      <Section title="Terms">
        <label className="flex flex-col gap-1">
          <span className="t-caption text-muted">Terms (shown on every quote)</span>
          <textarea
            rows={4}
            disabled={!canEdit}
            value={b.terms ?? ''}
            placeholder={defaultQuoteTerms(b.validityDays ?? DEFAULT_VALIDITY_DAYS)}
            onChange={(e) => set({ terms: e.target.value })}
            className="rounded-sm border border-border bg-surface2 px-3 py-2 text-[15px] text-text outline-none focus:border-accent"
          />
        </label>
        <div className="flex flex-wrap gap-3">
          <Field
            label="Valid for"
            className="w-36"
            inputMode="numeric"
            suffix="days"
            disabled={!canEdit}
            value={b.validityDays ?? ''}
            placeholder={String(DEFAULT_VALIDITY_DAYS)}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              set({ validityDays: Number.isFinite(v) && v > 0 ? Math.min(365, v) : undefined });
            }}
          />
          <Field
            label="Deposit"
            className="w-36"
            inputMode="decimal"
            suffix="%"
            disabled={!canEdit || !templates}
            value={b.depositPercent ?? ''}
            placeholder="none"
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value.replace(',', '.'));
              set({ depositPercent: Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : null });
            }}
          />
        </div>
      </Section>
      {canEdit ? (
        <Button
          variant="primary"
          icon={Save}
          loading={busy === 'save'}
          className="self-start"
          onClick={async () => {
            setBusy('save');
            try {
              const saved = await saveCatalog(scope, { branding: b });
              setB(brandingOf(saved) ?? b);
              toast('Branding saved', 'success');
            } catch (e) {
              if (!handleFeatureError(e, scope)) toast(apiMessage(e, 'Could not save the branding.'), 'error');
            } finally {
              setBusy(null);
            }
          }}
        >
          Save branding
        </Button>
      ) : null}
    </div>
  );
}
